import { CircleCheck, TriangleAlert } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Select } from '@/components/Select';
import { themeVars } from '@/styles/theme.css';
import * as styles from './index.css';

export type MappingFieldOption = {
    id: string;
    label: string;
};

type MappingFieldFormData = {
    pairs: { previousId: string | null }[];
};

type MappingFieldProps = {
    label: string;
    index: number;
    previousId: string | null;
    previousOptions: MappingFieldOption[];
};

/**
 * One row of a mapping form: the current item's name, a `Select` for its previous-survey
 * counterpart, and a matched/unmatched indicator. Shared by the category/question/attribute/
 * option mapping lists, which all register into `pairs.${index}.previousId`.
 */
export function MappingField({ label, index, previousId, previousOptions }: MappingFieldProps) {
    const {
        register,
        formState: { errors },
    } = useFormContext<MappingFieldFormData>();

    const isMatched = previousId !== null && previousId.trim() !== '';

    return (
        <Form.Field
            label={label}
            control={(props) => (
                <Flex align="center" gap="4">
                    <Select
                        {...props}
                        {...register(`pairs.${index}.previousId`)}
                        // TODO: `Select` only exposes `className` on its wrapper `div`, not the
                        // `<select>` itself, so a vanilla-extract class targeting `& > select`
                        // has no effect here. Overriding the border color inline until `Select`
                        // grows a way to reach the inner element.
                        style={{ borderColor: isMatched ? themeVars.color.green[600] : themeVars.color.peach[500] }}
                    >
                        <option value="">新規（一致している選択肢がありません）</option>
                        {previousOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.label}
                            </option>
                        ))}
                    </Select>

                    <div className={styles.checkmarkColumn[isMatched ? 'matched' : 'unmatched']}>
                        <Icon icon={isMatched ? CircleCheck : TriangleAlert} size="md" />
                    </div>
                </Flex>
            )}
            error={errors?.pairs?.[index]?.previousId?.message}
        />
    );
}
